<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
        <div class="flex items-center gap-3">
          <div class="p-2.5 bg-[#22282F]/10 rounded-xl">
            <History class="w-6 h-6 text-[#22282F]" />
          </div>
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-dashboard-text">Audit & Traçabilité</h1>
            <p class="text-sm text-dashboard-muted mt-0.5">
              Qui a fait quoi, sur quoi, quand, comment et depuis où.
            </p>
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <router-link
            to="/audit/archives"
            class="px-4 py-2 bg-white border border-dashboard-border text-dashboard-text rounded-lg text-sm font-medium hover:bg-dashboard-bg transition-colors flex items-center gap-2"
          >
            <ArchiveIcon class="w-4 h-4" />
            Archives MinIO
          </router-link>
          <button
            @click="downloadCsv"
            :disabled="loading"
            class="px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#22282F]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            title="Export CSV (utilise le streaming backend, supporte de grandes quantités)"
          >
            <Download class="w-4 h-4" />
            Export CSV
          </button>
          <button
            @click="fetchLogs"
            :disabled="loading"
            class="p-2 bg-white border border-dashboard-border rounded-lg hover:bg-dashboard-bg disabled:opacity-50"
            title="Actualiser"
          >
            <RefreshCw :class="['w-4 h-4', loading && 'animate-spin']" />
          </button>
        </div>
      </div>

      <!-- Stats cards -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Entrées chargées" :value="logs.length.toString()" :icon="Activity" tone="slate" />
        <StatCard label="Avertissements" :value="severityCounts.warning.toString()" :icon="AlertTriangle" tone="amber" />
        <StatCard label="Critiques" :value="severityCounts.critical.toString()" :icon="ShieldAlert" tone="red" />
        <StatCard label="Utilisateurs uniques" :value="uniqueUsersCount.toString()" :icon="Users" tone="indigo" />
      </div>

      <!-- Filters bar -->
      <div class="bg-white border border-dashboard-border rounded-xl shadow-sm p-4 mb-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <!-- Search -->
          <div class="relative lg:col-span-2">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dashboard-muted" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Recherche (utilisateur, IP, request_id…)"
              class="w-full pl-9 pr-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F]"
            />
          </div>

          <!-- Action -->
          <select v-model="filterAction" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F]">
            <option value="">Toutes les actions</option>
            <optgroup v-for="group in ACTION_GROUPS" :key="group.domain" :label="group.label">
              <option v-for="a in group.actions" :key="a" :value="a">{{ getActionLabel(a) }}</option>
            </optgroup>
          </select>

          <!-- Severity -->
          <select v-model="filterSeverity" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F]">
            <option value="">Toute sévérité</option>
            <option value="info">Information</option>
            <option value="warning">Avertissement</option>
            <option value="critical">Critique</option>
          </select>

          <!-- Source -->
          <select v-model="filterSource" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F]">
            <option value="">Toutes sources</option>
            <option v-for="(meta, key) in SOURCE_META" :key="key" :value="key">{{ meta.label }}</option>
          </select>

          <!-- Target type -->
          <select v-model="filterTargetType" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F]">
            <option value="">Toute cible</option>
            <option value="courrier">Courrier</option>
            <option value="note">Note</option>
            <option value="affectation">Affectation</option>
            <option value="user">Utilisateur</option>
            <option value="role">Rôle</option>
            <option value="cable">Câble</option>
          </select>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
          <input v-model="filterDateFrom" type="date" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm" placeholder="Du" />
          <input v-model="filterDateTo" type="date" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm" placeholder="Au" />
          <input v-model="filterIp" type="text" placeholder="Adresse IP exacte" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm" />
          <input v-model="filterRequestId" type="text" placeholder="UUID request_id" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm font-mono" />
          <input v-model="filterUserId" type="number" placeholder="ID utilisateur" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm" />
          <button
            v-if="hasActiveFilters"
            @click="clearFilters"
            class="px-3 py-2 text-dashboard-muted hover:text-dashboard-text text-sm flex items-center gap-2 justify-center border border-dashed border-dashboard-border rounded-lg hover:bg-dashboard-bg"
          >
            <X class="w-4 h-4" /> Réinitialiser
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading && logs.length === 0" class="bg-white border border-dashboard-border rounded-xl shadow-sm p-16">
        <div class="text-center">
          <div class="w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-4" />
          <p class="text-dashboard-muted">Chargement des logs…</p>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p class="font-medium text-red-800">Erreur de chargement</p>
          <p class="text-sm text-red-600 mt-1">{{ error }}</p>
        </div>
      </div>

      <!-- Table -->
      <div v-else-if="logs.length > 0" class="bg-white border border-dashboard-border rounded-xl shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-dashboard-bg border-b border-dashboard-border text-xs uppercase tracking-wide text-dashboard-muted">
              <tr>
                <th class="text-left py-3 px-4 font-semibold w-44">Date</th>
                <th class="text-left py-3 px-4 font-semibold">Utilisateur</th>
                <th class="text-left py-3 px-4 font-semibold">Action</th>
                <th class="text-left py-3 px-4 font-semibold">Cible</th>
                <th class="text-left py-3 px-4 font-semibold w-32">IP</th>
                <th class="text-left py-3 px-4 font-semibold w-24">Source</th>
                <th class="text-center py-3 px-4 font-semibold w-12"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="log in logs"
                :key="log.id"
                @click="selectedLog = log"
                :class="[
                  'border-t border-dashboard-border hover:bg-dashboard-bg cursor-pointer transition-colors border-l-4',
                  getSeverityMeta(log.severity).ring,
                  selectedLog?.id === log.id && 'bg-blue-50/40',
                ]"
              >
                <td class="py-3 px-4 align-top">
                  <div class="text-dashboard-text font-medium">{{ formatDateTime(log.created_at) }}</div>
                  <div class="text-xs text-dashboard-muted">{{ formatRelative(log.created_at) }}</div>
                </td>
                <td class="py-3 px-4 align-top">
                  <template v-if="log.user">
                    <div class="flex items-center gap-2">
                      <div class="w-8 h-8 rounded-full bg-[#22282F]/10 flex items-center justify-center text-xs font-semibold text-[#22282F]">
                        {{ getUserInitials(log.user) }}
                      </div>
                      <div class="min-w-0">
                        <p class="text-dashboard-text font-medium truncate">{{ log.user.full_name }}</p>
                        <router-link
                          :to="`/audit/user/${log.user.id}`"
                          @click.stop
                          class="text-xs text-blue-600 hover:underline"
                        >@{{ log.user.username }}</router-link>
                      </div>
                    </div>
                  </template>
                  <span v-else class="text-dashboard-muted italic text-xs">— Système / anonyme —</span>
                </td>
                <td class="py-3 px-4 align-top">
                  <div class="flex items-center gap-2">
                    <component
                      :is="getDomainMeta(log.action).icon"
                      :class="['w-4 h-4', 'text-' + 'dashboard-muted']"
                    />
                    <div class="flex flex-col gap-0.5">
                      <span class="text-dashboard-text font-medium">{{ getActionLabel(log.action) }}</span>
                      <div class="flex items-center gap-1.5">
                        <span :class="['text-[10px] px-1.5 py-0.5 rounded font-medium', getDomainMeta(log.action).badge]">
                          {{ getDomainMeta(log.action).label }}
                        </span>
                        <span v-if="log.severity !== 'info'" :class="['text-[10px] px-1.5 py-0.5 rounded font-medium', getSeverityMeta(log.severity).badge]">
                          {{ getSeverityMeta(log.severity).label }}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="py-3 px-4 align-top">
                  <template v-if="log.courrier_id || log.target_id">
                    <router-link
                      v-if="log.courrier_id || log.target_type === 'courrier'"
                      :to="`/audit/courrier/${log.courrier_id || log.target_id}`"
                      @click.stop
                      class="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs hover:bg-indigo-100 border border-indigo-200"
                    >
                      <Mail class="w-3 h-3" /> Courrier #{{ log.courrier_id || log.target_id }}
                    </router-link>
                    <span v-else class="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">
                      <component :is="ListChecks" class="w-3 h-3" />
                      {{ getTargetLabel(log.target_type) }} #{{ log.target_id }}
                    </span>
                  </template>
                  <span v-else class="text-dashboard-muted italic text-xs">—</span>
                </td>
                <td class="py-3 px-4 align-top">
                  <span v-if="log.ip_address" class="font-mono text-xs text-dashboard-text">{{ log.ip_address }}</span>
                  <span v-else class="text-dashboard-muted italic text-xs">—</span>
                </td>
                <td class="py-3 px-4 align-top">
                  <span :class="['inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', getSourceMeta(log.source).badge]">
                    <component :is="getSourceMeta(log.source).icon" class="w-3 h-3" />
                    {{ log.source }}
                  </span>
                </td>
                <td class="py-3 px-4 text-center align-top">
                  <ChevronRight class="w-4 h-4 text-dashboard-muted inline" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="border-t border-dashboard-border px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div class="text-sm text-dashboard-muted">
            Page <strong>{{ pagination.page }}</strong> / <strong>{{ pagination.pages || 1 }}</strong>
            <span class="ml-3">{{ pagination.total }} entrée(s) au total</span>
          </div>
          <div class="flex items-center gap-2">
            <select v-model.number="perPage" @change="fetchLogs" class="px-2 py-1 border border-dashboard-border rounded text-sm">
              <option :value="25">25 / page</option>
              <option :value="50">50 / page</option>
              <option :value="100">100 / page</option>
              <option :value="200">200 / page</option>
            </select>
            <button
              @click="goPrev"
              :disabled="pagination.page <= 1"
              class="px-3 py-1 border border-dashboard-border rounded text-sm hover:bg-dashboard-bg disabled:opacity-50 disabled:cursor-not-allowed"
            >Précédent</button>
            <button
              @click="goNext"
              :disabled="pagination.page >= pagination.pages"
              class="px-3 py-1 border border-dashboard-border rounded text-sm hover:bg-dashboard-bg disabled:opacity-50 disabled:cursor-not-allowed"
            >Suivant</button>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else class="bg-white border border-dashboard-border rounded-xl shadow-sm p-12 text-center">
        <div class="w-16 h-16 bg-dashboard-bg rounded-full flex items-center justify-center mx-auto mb-4">
          <History class="w-8 h-8 text-dashboard-muted" />
        </div>
        <h3 class="text-lg font-semibold text-dashboard-text mb-1">Aucun log trouvé</h3>
        <p class="text-dashboard-muted text-sm">
          {{ hasActiveFilters ? 'Essayez d\'élargir vos filtres.' : 'Aucune activité enregistrée pour le moment.' }}
        </p>
      </div>
    </div>

    <!-- Side detail panel -->
    <AuditLogDetailPanel
      v-if="selectedLog"
      :log="selectedLog"
      @close="selectedLog = null"
    />
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import {
  History, Activity, Search, RefreshCw, AlertCircle, X, Users, AlertTriangle,
  ShieldAlert, Mail, Info, Download, ChevronRight, ListChecks, Archive as ArchiveIcon,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AuditLogDetailPanel from '@/components/audit/AuditLogDetailPanel.vue'
import StatCard from '@/components/audit/StatCard.vue'
import { apiFetch, API_BASE_URL } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import {
  ACTION_GROUPS, SOURCE_META,
  getActionLabel, getDomainMeta, getTargetLabel,
  getSeverityMeta, getSourceMeta,
  formatDateTime, formatRelative,
} from '@/utils/auditMeta'
import type { AuditLogEntry } from '@/utils/auditMeta'

const toast = useToast()

const logs = ref<AuditLogEntry[]>([])
const loading = ref(false)
const error = ref('')
const selectedLog = ref<AuditLogEntry | null>(null)

// Filtres (state)
const searchQuery = ref('')
const filterAction = ref('')
const filterSeverity = ref('')
const filterSource = ref('')
const filterTargetType = ref('')
const filterDateFrom = ref('')
const filterDateTo = ref('')
const filterIp = ref('')
const filterRequestId = ref('')
const filterUserId = ref<number | string>('')

// Pagination
const perPage = ref(50)
const pagination = ref({ page: 1, per_page: 50, total: 0, pages: 0 })

const hasActiveFilters = computed(() =>
  Boolean(
    searchQuery.value || filterAction.value || filterSeverity.value || filterSource.value ||
    filterTargetType.value || filterDateFrom.value || filterDateTo.value || filterIp.value ||
    filterRequestId.value || filterUserId.value,
  ),
)

const severityCounts = computed(() => {
  const c = { info: 0, warning: 0, critical: 0 }
  for (const l of logs.value) c[l.severity] = (c[l.severity] || 0) + 1
  return c
})

const uniqueUsersCount = computed(() =>
  new Set(logs.value.filter(l => l.user).map(l => l.user!.id)).size,
)

function getUserInitials(user: { full_name: string; username: string }): string {
  const name = user.full_name || user.username
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map(p => p[0]?.toUpperCase() || '').join('') || '?'
}

function buildQueryString(forCsv = false): string {
  const params = new URLSearchParams()
  if (searchQuery.value.trim()) params.set('q', searchQuery.value.trim())
  if (filterAction.value) params.set('action', filterAction.value)
  if (filterSeverity.value) params.set('severity', filterSeverity.value)
  if (filterSource.value) params.set('source', filterSource.value)
  if (filterTargetType.value) params.set('target_type', filterTargetType.value)
  if (filterDateFrom.value) params.set('from', `${filterDateFrom.value}T00:00:00`)
  if (filterDateTo.value) params.set('to', `${filterDateTo.value}T23:59:59`)
  if (filterIp.value) params.set('ip', filterIp.value)
  if (filterRequestId.value) params.set('request_id', filterRequestId.value)
  if (filterUserId.value) params.set('user_id', String(filterUserId.value))
  if (forCsv) {
    params.set('format', 'csv')
  } else {
    params.set('page', String(pagination.value.page))
    params.set('per_page', String(perPage.value))
  }
  return params.toString()
}

async function fetchLogs() {
  loading.value = true
  error.value = ''
  try {
    const qs = buildQueryString()
    const result = await apiFetch(`/api/admin/audit-logs/?${qs}`)
    logs.value = result.data || []
    pagination.value = result.pagination || { page: 1, per_page: perPage.value, total: 0, pages: 0 }
  } catch (e: any) {
    error.value = e?.message || 'Erreur lors du chargement'
    toast.error('Erreur', error.value)
  } finally {
    loading.value = false
  }
}

function clearFilters() {
  searchQuery.value = ''
  filterAction.value = ''
  filterSeverity.value = ''
  filterSource.value = ''
  filterTargetType.value = ''
  filterDateFrom.value = ''
  filterDateTo.value = ''
  filterIp.value = ''
  filterRequestId.value = ''
  filterUserId.value = ''
  pagination.value.page = 1
  fetchLogs()
}

function goPrev() {
  if (pagination.value.page > 1) {
    pagination.value.page--
    fetchLogs()
  }
}

function goNext() {
  if (pagination.value.page < pagination.value.pages) {
    pagination.value.page++
    fetchLogs()
  }
}

function downloadCsv() {
  const qs = buildQueryString(true)
  // Le backend stream le CSV ; on déclenche un téléchargement natif avec
  // cookies de session (window.location respecte les cookies).
  window.open(`${API_BASE_URL}/api/admin/audit-logs/?${qs}`, '_blank')
}

// Re-fetch automatique quand filtres serveur changent
watch(
  [filterAction, filterSeverity, filterSource, filterTargetType, filterDateFrom, filterDateTo, filterIp, filterRequestId, filterUserId],
  () => {
    pagination.value.page = 1
    fetchLogs()
  },
)

// Recherche plein-texte : debounce pour ne pas requêter à chaque frappe
let searchDebounce: ReturnType<typeof setTimeout> | undefined
watch(searchQuery, () => {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    pagination.value.page = 1
    fetchLogs()
  }, 350)
})

onMounted(fetchLogs)
</script>
